import Breadcrumb from '@/components/base/Breadcrumb';
import ContactForm from './components/ContactForm';
import Tag from '@/components/base/Tag';
import { siteName, siteStats } from '@/mocks/site';

const contentScope = [
  {
    icon: 'ri-film-line',
    title: '电影资源',
    desc: '收录剧情、科幻、悬疑、爱情、历史等多元类型电影，提供剧情梗概与主创信息。',
  },
  {
    icon: 'ri-tv-2-line',
    title: '电视剧资源',
    desc: '覆盖热播剧集与完结好剧，标注集数、播出状态与每集时长，方便追剧规划。',
  },
  {
    icon: 'ri-user-star-line',
    title: '演员资料',
    desc: '整理演员简介、出生地与代表作品，并支持从影视详情页直接跳转查看。',
  },
  {
    icon: 'ri-newspaper-line',
    title: '上映与热播资讯',
    desc: '按上映时间与站内热度排序，帮助你第一时间发现值得关注的新作。',
  },
];

const updateNotes = [
  '影视条目每日整理，新上映电影与开播剧集会及时补充到列表。',
  '演员资料与作品关联持续完善，发现遗漏可通过下方表单反馈。',
  '分类筛选、搜索与收藏均为前端演示功能，数据为示范内容。',
  '本站不提供在线播放，仅用于剧情介绍、资料查阅与内容推荐展示。',
];

export default function AboutPage() {
  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20elegant%20dark%20movie%20theater%20with%20rows%20of%20seats%20and%20a%20glowing%20screen%2C%20dramatic%20amber%20and%20deep%20blue%20lighting%2C%20atmospheric%20mood%2C%20no%20text&width=1600&height=600&seq=about-hero-banner-01&orientation=landscape"
          alt="关于美女与野兽"
          title="关于我们"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/60" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">关于我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            {siteName} 是一个专注于影视资源展示与推荐的平台，我们相信每一个好故事都值得被认真介绍。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-semibold text-accent-900">
              <i className="ri-compass-3-line" aria-hidden="true" />
              网站定位
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
              面向影迷的影视资料与推荐平台
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              {siteName} 为影视爱好者、影评人与追剧用户而建。我们把分散在各处的影视资料整理到一起，
              让你可以用更少的时间，找到更想看的内容。无论是想快速了解一部电影的剧情，还是想系统地浏览某位演员的作品，
              这里都能提供清晰、连贯的浏览体验。
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Tag tone="secondary">内容展示</Tag>
              <Tag tone="accent">资料查阅</Tag>
              <Tag tone="primary">个性化推荐</Tag>
              <Tag tone="muted">本地收藏</Tag>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {siteStats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="font-heading text-2xl font-black text-primary-600">{stat.value}</p>
                <p className="mt-1 font-label text-xs text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
            内容范围
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {contentScope.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-2xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
            内容及更新说明
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700">
            为了保证浏览体验与信息一致性，{siteName} 对内容整理与更新制定了统一规则：
          </p>
          <ul className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
            {updateNotes.map((note) => (
              <li key={note} className="flex items-start gap-3 rounded-md bg-background-50 p-4">
                <i className="ri-checkbox-circle-fill mt-0.5 text-lg text-accent-500" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-foreground-700">{note}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-6 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              联系我们
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700">
              如果你对平台内容有任何建议，或希望与我们展开合作，欢迎随时联系。我们会认真阅读每一条留言。
            </p>
            <div className="mt-6 flex flex-col gap-4">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: 'hello@beautyandbeast.film' },
                { icon: 'ri-map-pin-line', label: '办公地址', value: '示例市 · 影视文化产业园 A 座 12 层' },
                { icon: 'ri-time-line', label: '回复时间', value: '工作日 9:00 - 18:00' },
              ].map((contact) => (
                <div key={contact.label} className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${contact.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block font-label text-xs text-foreground-500">{contact.label}</span>
                    <span className="block text-sm font-medium text-foreground-900">{contact.value}</span>
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 p-3 text-xs leading-relaxed text-foreground-600">
              <i className="ri-information-line mr-1 text-accent-500" aria-hidden="true" />
              以上联系方式为演示信息，表单提交为真实可用的留言收集渠道。
            </p>
          </div>

          <ContactForm />
        </section>
      </div>
    </div>
  );
}